import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ordenesCompraAPI } from '../services/ordenesCompraAPI';
import { ingredientesKeys } from './useIngredientes';
import { lotesKeys } from './useLotes';

// Query keys para React Query
export const ordenesCompraKeys = {
  all: ['ordenes-compra'] as const,
  lists: () => [...ordenesCompraKeys.all, 'list'] as const,
  list: (params?: any) => [...ordenesCompraKeys.lists(), params] as const,
  details: () => [...ordenesCompraKeys.all, 'detail'] as const,
  detail: (id: string) => [...ordenesCompraKeys.details(), id] as const,
  sugerencias: () => [...ordenesCompraKeys.all, 'sugerencias'] as const,
};

// Hook para listar órdenes de compra
export const useOrdenesCompra = (params?: {
  estado?: string;
  proveedor?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ordenesCompraKeys.list(params),
    queryFn: () => ordenesCompraAPI.listar(params),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

// Hook para obtener una orden específica
export const useOrdenCompra = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ordenesCompraKeys.detail(id),
    queryFn: () => ordenesCompraAPI.obtener(id),
    enabled: enabled && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

// Hook para obtener sugerencias de órdenes
export const useSugerenciasOrden = () => {
  return useQuery({
    queryKey: ordenesCompraKeys.sugerencias(),
    queryFn: () => ordenesCompraAPI.obtenerSugerencias(),
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
};

// Hook para crear orden de compra
export const useCrearOrdenCompra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ordenesCompraAPI.crear,
    onSuccess: () => {
      // Invalidaciones cruzadas: crear OC afecta el listado de OCs y las
      // sugerencias (la cantidad_ya_solicitada cambió). No toca stock ni
      // lotes hasta que se recepcione, así que esos no se invalidan acá.
      queryClient.invalidateQueries({
        queryKey: ordenesCompraKeys.all,
        refetchType: 'all',
      });
      queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.sugerencias() });
    },
    onError: (error) => {
      console.error('Error al crear orden de compra:', error);
    },
  });
};

// Hook para actualizar orden de compra
export const useActualizarOrdenCompra = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => ordenesCompraAPI.actualizar(id, data),
    onSuccess: () => {
      // Invalidar la orden específica y TODAS las listas
      queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.detail(id) });
      queryClient.invalidateQueries({
        queryKey: ordenesCompraKeys.all,
        refetchType: 'all',
      });
    },
    onError: (error) => {
      console.error('Error al actualizar orden de compra:', error);
    },
  });
};

// Hook para enviar orden de compra
export const useEnviarOrdenCompra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ordenesCompraAPI.enviar(id),
    onSuccess: (_, id) => {
      // Enviar también actualiza precios automáticamente en ingredientes
      // (ver actualizarPreciosIngredientesDesdeLaOrden en backend), así que
      // invalida ingredientes + sugerencias además del listado de OCs.
      queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.detail(id) });
      queryClient.invalidateQueries({
        queryKey: ordenesCompraKeys.all,
        refetchType: 'all',
      });
      queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.sugerencias() });
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
    },
    onError: (error) => {
      console.error('Error al enviar orden de compra:', error);
    },
  });
};

// Hook para cancelar orden de compra
export const useCancelarOrdenCompra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) =>
      ordenesCompraAPI.cancelar(id, motivo),
    onSuccess: (_, { id }) => {
      // Invalidar la orden específica y TODAS las listas
      queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.detail(id) });
      queryClient.invalidateQueries({
        queryKey: ordenesCompraKeys.all,
        refetchType: 'all',
      });
    },
    onError: (error) => {
      console.error('Error al cancelar orden de compra:', error);
    },
  });
};

// Hook para recepcionar orden de compra
export const useRecepcionarOrdenCompra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      ordenesCompraAPI.recepcionar(id, data),
    onSuccess: (_, { id }) => {
      // Recepcionar mueve datos en 4 dominios: OCs, sugerencias,
      // ingredientes (stock_actual sube) y lotes_ingredientes (lote nuevo).
      // Invalidamos los 4 para que /cocina, /lotes, /compras → Sugerencias
      // y la pestaña Órdenes vean el cambio sin necesidad de refresh manual.
      queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: ordenesCompraKeys.sugerencias(),
      });
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.all });
      queryClient.invalidateQueries({ queryKey: lotesKeys.all });
    },
    onError: (error) => {
      console.error('Error al recepcionar orden de compra:', error);
    },
  });
};

// Hook para eliminar orden de compra
export const useEliminarOrdenCompra = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ordenesCompraAPI.eliminar(id),
    onSuccess: (_, id) => {
      // Invalidar la lista de órdenes
      queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.lists() });
      // Remover la orden específica del cache
      queryClient.removeQueries({ queryKey: ordenesCompraKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Error al eliminar orden de compra:', error);
    },
  });
};
